/**
 * Action application.
 *
 * Move / Stretch / Flip / Rotate / Scale / Array / Lookup / PolarStretch
 * semantics per AutoCAD's Tier 1 + Tier 2 + Tier 3 dynamic block spec.
 *
 * Type-validity rules (enforced with error return rather than crashing):
 *   - Move          : parameter.kind ∈ LINEAR_LIKE
 *   - Stretch       : parameter.kind ∈ LINEAR_LIKE
 *   - Scale         : parameter.kind ∈ LINEAR_LIKE
 *   - Array         : parameter.kind ∈ LINEAR_LIKE
 *   - Rotate        : parameter.kind ∈ { rotation, constraint-angular }
 *   - Flip          : parameter.kind === "flip"
 *   - Lookup        : parameter.kind === "lookup"
 *   - PolarStretch  : parameter.kind === "polar" (Tier 3)
 *
 * where LINEAR_LIKE = { linear, polar, xy, constraint-horizontal,
 *                      constraint-vertical, constraint-aligned,
 *                      constraint-radial, constraint-diameter }.
 *
 * Any mismatch returns the selection unchanged and surfaces an error
 * string in `ActionApplication.errors`.
 *
 * Tier 2 (DYN-2) additions:
 *   - Rotate: rotate selection around rotation.basePoint by
 *     (currentAngle - defaultAngle) * angleMultiplier.
 *   - Scale:  scale selection from parameter base with uniform / x-only /
 *     y-only factor.
 *   - Array:  distribute copies of selection along parameter axis.
 *     Linear → N columns along parameter direction.
 *     Polar  → N radial copies.
 *     XY     → cols × rows grid.
 *   - Lookup: no geometry change; emits `runtimeValueUpdates` telling the
 *     evaluator to rewrite OTHER parameters' currents based on the lookup
 *     row's input values. Evaluator re-evaluates on update.
 *
 * Tier 2 Batch C (DYN-2 constraints) additions:
 *   - constraint-horizontal / constraint-vertical: drive Move/Stretch/
 *     Scale/Array along their fixed axis (X for horizontal, Y for
 *     vertical). Direct-propagation only (full Gauss-Newton solver
 *     lands in DYN-3 B).
 *   - constraint-angular: drives Rotate around the constraint vertex
 *     by (currentAngle - defaultAngle) * angleMultiplier (degrees).
 *
 * Tier 3 (DYN-3) additions:
 *   - PolarStretch: combined rotate + translate + stretch, authored on a
 *     polar parameter. Rotates vertices inside crossingFrame by
 *     (currentAngle - defaultAngle) around the polar base, then
 *     translates them by (currentDistance - defaultDistance) along the
 *     polar direction.
 *   - constraint-aligned: 1D Euclidean-distance driver along an
 *     arbitrary axis (pointA → pointB direction).
 *   - constraint-radial / constraint-diameter: 1D radius / diameter
 *     drivers; Scale actions use them as uniform scale factors around
 *     the constraint's `center`.
 */

import type {
  AlignedConstraintParameter,
  AngularConstraintParameter,
  ArrayAction,
  DiameterConstraintParameter,
  DynamicAction,
  DynamicParameter,
  FlipAction,
  FlipParameter,
  HorizontalConstraintParameter,
  LinearParameter,
  LookupAction,
  LookupParameter,
  LookupTable,
  MoveAction,
  PolarParameter,
  PolarStretchAction,
  RadialConstraintParameter,
  RotateAction,
  RotationParameter,
  ScaleAction,
  StretchAction,
  VerticalConstraintParameter,
  XYParameter,
} from "@dxf-ai-editor/drawing-model";
import {
  findLookupRowById,
  findLookupTableById,
} from "@dxf-ai-editor/drawing-model";

import type { EntityLike, Vec2 } from "./geometry";
import {
  mirrorEntity,
  rotateEntity,
  scaleEntity,
  stretchEntity,
  translateEntity,
} from "./geometry";
import type { ParameterRuntimeValue } from "./parameters";
import { applyPolarStretch } from "./actions.polarStretch";
// Bilingual wording for a declined heavy child; its own leaf module so this
// file stays under the 1000-line guideline (see that module's header).
import { refusalSink } from "./actionRefusalMessages";

export interface ActionContext {
  readonly runtimeValues: ReadonlyMap<string, ParameterRuntimeValue>;
  readonly parameters: readonly DynamicParameter[];
  /** Lookup tables for this block — only the Lookup action consumes this. */
  readonly lookupTables?: readonly LookupTable[];
}

/**
 * A Lookup action can request updates to OTHER parameters' current
 * values. The evaluator applies these updates and re-runs the action
 * pipeline so geometry reacts. Keys are parameter ids; values are the
 * new currents typed per kind (number / string / boolean).
 */
export interface RuntimeValueUpdate {
  readonly parameterId: string;
  /** Matches schema property names: currentDistance / currentAngle / etc. */
  readonly property: string;
  readonly value: number | string | boolean;
}

export interface ActionApplication {
  readonly entities: readonly EntityLike[];
  readonly errors: readonly string[];
  readonly runtimeValueUpdates?: readonly RuntimeValueUpdate[];
}

/* ── helpers ────────────────────────────────────────────────────── */

function actionLabel(a: DynamicAction): string {
  return a.name && a.name.length > 0 ? a.name : a.id;
}

const DEG_TO_RAD = Math.PI / 180;

function vecScale(v: Vec2, k: number): Vec2 {
  return { x: v.x * k, y: v.y * k };
}

/**
 * Returns true when the parameter kind is compatible with the linear-like
 * action family (Move / Stretch / Scale / Array). Tier 2 constraint kinds
 * `constraint-horizontal` and `constraint-vertical` and Tier 3 constraint
 * kinds `constraint-aligned`, `constraint-radial`, `constraint-diameter`
 * join Tier 1 / Tier 2 numeric kinds in this set.
 */
function isLinearLikeKind(kind: DynamicParameter["kind"]): boolean {
  return (
    kind === "linear" ||
    kind === "polar" ||
    kind === "xy" ||
    kind === "constraint-horizontal" ||
    kind === "constraint-vertical" ||
    kind === "constraint-aligned" ||
    kind === "constraint-radial" ||
    kind === "constraint-diameter"
  );
}

/**
 * Returns true when the parameter kind drives a Rotate action. Includes
 * Tier 2 rotation + Tier 2 constraint-angular.
 */
function isAngularKind(kind: DynamicParameter["kind"]): boolean {
  return kind === "rotation" || kind === "constraint-angular";
}

/**
 * Return the linear-like driving vector (delta) for a given parameter,
 * runtime, and action multipliers. Used by Move / Stretch.
 *
 * Supported parameter kinds:
 *   linear: delta = (currentDistance - defaultDistance) *
 *                   (cos(axisAngle + offset), sin(axisAngle + offset)) *
 *                   multiplier
 *   polar : delta = (currentDistance * (cos(currentAngle), sin(currentAngle)))
 *                 - (defaultDistance * (cos(defaultAngle), sin(defaultAngle)))
 *                   then rotated by offset and scaled by multiplier
 *   xy    : delta = (currentX - defaultX, currentY - defaultY)
 *                   (offset / multiplier apply as scalar rotation / scale)
 *
 * `directionOverrideDeg` (Stretch's `stretchDirection`) replaces the
 * parameter's own direction when non-zero. For xy / polar the override
 * overrides the effective angle before applying magnitude; for linear it
 * replaces axisAngle.
 */
function computeLinearLikeDelta(
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
  multiplier: number,
  angleOffsetDeg: number,
  directionOverrideDeg?: number,
): Vec2 {
  const offsetRad = angleOffsetDeg * DEG_TO_RAD;
  const directionOverrideSet =
    directionOverrideDeg != null && directionOverrideDeg !== 0;

  if (parameter.kind === "linear") {
    const lin = parameter as LinearParameter;
    const distance = runtime.distance ?? lin.currentDistance;
    const deltaDist = distance - lin.defaultDistance;
    const baseAngleRad = directionOverrideSet
      ? (directionOverrideDeg as number) * DEG_TO_RAD
      : (runtime.angle ?? 0);
    const total = baseAngleRad + offsetRad;
    const magnitude = deltaDist * multiplier;
    return { x: magnitude * Math.cos(total), y: magnitude * Math.sin(total) };
  }

  if (parameter.kind === "polar") {
    const pol = parameter as PolarParameter;
    const defAngleRad = pol.defaultAngle * DEG_TO_RAD;
    const curAngleRad = runtime.angle ?? pol.currentAngle * DEG_TO_RAD;
    const defDist = pol.defaultDistance;
    const curDist = runtime.distance ?? pol.currentDistance;
    // Delta from (defDist·cos,defDist·sin) → (curDist·cos,curDist·sin).
    const dx =
      curDist * Math.cos(curAngleRad) - defDist * Math.cos(defAngleRad);
    const dy =
      curDist * Math.sin(curAngleRad) - defDist * Math.sin(defAngleRad);
    // Apply offset rotation around origin and multiplier scaling.
    const c = Math.cos(offsetRad);
    const s = Math.sin(offsetRad);
    const mag = multiplier;
    const rdx = (dx * c - dy * s) * mag;
    const rdy = (dx * s + dy * c) * mag;
    if (directionOverrideSet) {
      // Keep magnitude; replace direction with the override.
      const magnitude = Math.hypot(rdx, rdy);
      const ang = (directionOverrideDeg as number) * DEG_TO_RAD;
      return { x: magnitude * Math.cos(ang), y: magnitude * Math.sin(ang) };
    }
    return { x: rdx, y: rdy };
  }

  if (parameter.kind === "xy") {
    const xy = parameter as XYParameter;
    const dx = (runtime.xy?.x ?? xy.currentX) - xy.defaultX;
    const dy = (runtime.xy?.y ?? xy.currentY) - xy.defaultY;
    const c = Math.cos(offsetRad);
    const s = Math.sin(offsetRad);
    let rdx = (dx * c - dy * s) * multiplier;
    let rdy = (dx * s + dy * c) * multiplier;
    if (directionOverrideSet) {
      const magnitude = Math.hypot(rdx, rdy);
      const ang = (directionOverrideDeg as number) * DEG_TO_RAD;
      rdx = magnitude * Math.cos(ang);
      rdy = magnitude * Math.sin(ang);
    }
    return { x: rdx, y: rdy };
  }

  if (
    parameter.kind === "constraint-horizontal" ||
    parameter.kind === "constraint-vertical"
  ) {
    // Horizontal / Vertical constraints behave as 1D linear drivers
    // along their fixed axis (X for horizontal, Y for vertical).
    // Direct propagation: delta = (currentDistance - defaultDistance) *
    // multiplier, projected onto the constraint axis (X or Y), then
    // rotated by `angleOffset` and possibly overridden by
    // `directionOverrideDeg` (Stretch).
    const c = parameter as
      | HorizontalConstraintParameter
      | VerticalConstraintParameter;
    const distance = runtime.distance ?? c.currentDistance;
    const deltaDist = distance - c.defaultDistance;
    // Author axis: 0 for horizontal (+X), π/2 for vertical (+Y).
    const baseAxisRad =
      parameter.kind === "constraint-horizontal" ? 0 : Math.PI / 2;
    const baseAngleRad = directionOverrideSet
      ? (directionOverrideDeg as number) * DEG_TO_RAD
      : baseAxisRad;
    const total = baseAngleRad + offsetRad;
    const magnitude = deltaDist * multiplier;
    return { x: magnitude * Math.cos(total), y: magnitude * Math.sin(total) };
  }

  if (parameter.kind === "constraint-aligned") {
    // DYN-3 Tier 3: Aligned distance driver — project delta along the
    // authored pointA → pointB direction (runtime.angle already carries
    // the aligned axis in radians).
    const c = parameter as AlignedConstraintParameter;
    const distance = runtime.distance ?? c.currentDistance;
    const deltaDist = distance - c.defaultDistance;
    const baseAngleRad = directionOverrideSet
      ? (directionOverrideDeg as number) * DEG_TO_RAD
      : (runtime.angle ?? 0);
    const total = baseAngleRad + offsetRad;
    const magnitude = deltaDist * multiplier;
    return { x: magnitude * Math.cos(total), y: magnitude * Math.sin(total) };
  }

  if (parameter.kind === "constraint-radial") {
    // DYN-3 Tier 3: Radial constraint drives delta along the authored
    // radial direction (center → radiusPoint). Delta = (currentRadius -
    // defaultRadius) * multiplier.
    const c = parameter as RadialConstraintParameter;
    const distance = runtime.distance ?? c.currentRadius;
    const deltaDist = distance - c.defaultRadius;
    const baseAngleRad = directionOverrideSet
      ? (directionOverrideDeg as number) * DEG_TO_RAD
      : (runtime.angle ?? 0);
    const total = baseAngleRad + offsetRad;
    const magnitude = deltaDist * multiplier;
    return { x: magnitude * Math.cos(total), y: magnitude * Math.sin(total) };
  }

  if (parameter.kind === "constraint-diameter") {
    // DYN-3 Tier 3: Diameter constraint drives delta along the authored
    // diameter direction (point1 → point2). Delta = (currentDiameter -
    // defaultDiameter) * multiplier.
    const c = parameter as DiameterConstraintParameter;
    const distance = runtime.distance ?? c.currentDiameter;
    const deltaDist = distance - c.defaultDiameter;
    const baseAngleRad = directionOverrideSet
      ? (directionOverrideDeg as number) * DEG_TO_RAD
      : (runtime.angle ?? 0);
    const total = baseAngleRad + offsetRad;
    const magnitude = deltaDist * multiplier;
    return { x: magnitude * Math.cos(total), y: magnitude * Math.sin(total) };
  }

  return { x: 0, y: 0 };
}

/**
 * Parameter "center" used by rotate / scale actions as the fixed pivot
 * point. Matches AutoCAD's "parameter base point" semantics.
 */
function parameterCenter(parameter: DynamicParameter): Vec2 {
  switch (parameter.kind) {
    case "linear":
      return (parameter as LinearParameter).basePoint;
    case "polar":
      return (parameter as PolarParameter).basePoint;
    case "xy":
      return (parameter as XYParameter).basePoint;
    case "rotation":
      return (parameter as RotationParameter).basePoint;
    case "constraint-horizontal":
      return (parameter as HorizontalConstraintParameter).pointA;
    case "constraint-vertical":
      return (parameter as VerticalConstraintParameter).pointA;
    case "constraint-angular":
      return (parameter as AngularConstraintParameter).vertex;
    case "constraint-aligned":
      return (parameter as AlignedConstraintParameter).pointA;
    case "constraint-radial":
      return (parameter as RadialConstraintParameter).center;
    case "constraint-diameter":
      return (parameter as DiameterConstraintParameter).center;
    default:
      return { x: 0, y: 0 };
  }
}

/** Per-parameter scale factor (ratio of current / default). */
function parameterScaleFactor(
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
): { sx: number; sy: number } {
  if (parameter.kind === "linear") {
    const lin = parameter as LinearParameter;
    const cur = runtime.distance ?? lin.currentDistance;
    const base = lin.defaultDistance === 0 ? 1 : lin.defaultDistance;
    const f = lin.defaultDistance === 0 ? 1 : cur / base;
    return { sx: f, sy: f };
  }
  if (parameter.kind === "polar") {
    const pol = parameter as PolarParameter;
    const cur = runtime.distance ?? pol.currentDistance;
    const base = pol.defaultDistance === 0 ? 1 : pol.defaultDistance;
    const f = pol.defaultDistance === 0 ? 1 : cur / base;
    return { sx: f, sy: f };
  }
  if (parameter.kind === "xy") {
    const xy = parameter as XYParameter;
    const curX = runtime.xy?.x ?? xy.currentX;
    const curY = runtime.xy?.y ?? xy.currentY;
    const sx = xy.defaultX === 0 ? 1 : curX / xy.defaultX;
    const sy = xy.defaultY === 0 ? 1 : curY / xy.defaultY;
    return { sx, sy };
  }
  if (
    parameter.kind === "constraint-horizontal" ||
    parameter.kind === "constraint-vertical"
  ) {
    // Constraint scale factor — uniform along the constraint axis only.
    // Horizontal scales X; Vertical scales Y. The unaffected axis is
    // pinned to 1 so callers can request "uniform" / "x-only" / "y-only"
    // and still get sensible behavior.
    const c = parameter as
      | HorizontalConstraintParameter
      | VerticalConstraintParameter;
    const cur = runtime.distance ?? c.currentDistance;
    const base = c.defaultDistance === 0 ? 1 : c.defaultDistance;
    const f = c.defaultDistance === 0 ? 1 : cur / base;
    if (parameter.kind === "constraint-horizontal") {
      return { sx: f, sy: 1 };
    }
    return { sx: 1, sy: f };
  }
  if (parameter.kind === "constraint-aligned") {
    // DYN-3 Tier 3: Aligned constraint scales uniformly along the
    // authored aligned axis. Since the axis is arbitrary we apply the
    // factor uniformly to both sx and sy (the delta is applied in the
    // aligned direction via Move / Stretch when linear scaling alone
    // isn't enough).
    const c = parameter as AlignedConstraintParameter;
    const cur = runtime.distance ?? c.currentDistance;
    const base = c.defaultDistance === 0 ? 1 : c.defaultDistance;
    const f = c.defaultDistance === 0 ? 1 : cur / base;
    return { sx: f, sy: f };
  }
  if (parameter.kind === "constraint-radial") {
    // DYN-3 Tier 3: Radial constraint scales uniformly about `center`.
    // Factor = currentRadius / defaultRadius.
    const c = parameter as RadialConstraintParameter;
    const cur = runtime.distance ?? c.currentRadius;
    const base = c.defaultRadius === 0 ? 1 : c.defaultRadius;
    const f = c.defaultRadius === 0 ? 1 : cur / base;
    return { sx: f, sy: f };
  }
  if (parameter.kind === "constraint-diameter") {
    // DYN-3 Tier 3: Diameter constraint scales uniformly about `center`.
    // Factor = currentDiameter / defaultDiameter.
    const c = parameter as DiameterConstraintParameter;
    const cur = runtime.distance ?? c.currentDiameter;
    const base = c.defaultDiameter === 0 ? 1 : c.defaultDiameter;
    const f = c.defaultDiameter === 0 ? 1 : cur / base;
    return { sx: f, sy: f };
  }
  return { sx: 1, sy: 1 };
}

/* ── Dispatch ───────────────────────────────────────────────────── */

export function applyAction(
  action: DynamicAction,
  selection: readonly EntityLike[],
  ctx: ActionContext,
): ActionApplication {
  const errors: string[] = [];
  const parameter = ctx.parameters.find((p) => p.id === action.parameterId);
  if (!parameter) {
    errors.push(
      `Action "${actionLabel(action)}" references unknown parameter ${action.parameterId}`,
    );
    return { entities: selection, errors };
  }
  const runtime = ctx.runtimeValues.get(action.parameterId);
  if (!runtime) {
    errors.push(
      `Action "${actionLabel(action)}" missing runtime value for parameter ${action.parameterId}`,
    );
    return { entities: selection, errors };
  }

  switch (action.kind) {
    case "move":
      return applyMove(action, parameter, runtime, selection, errors);
    case "stretch":
      return applyStretch(action, parameter, runtime, selection, errors);
    case "flip":
      return applyFlip(action, parameter, selection, errors);
    case "rotate":
      return applyRotate(action, parameter, runtime, selection, errors);
    case "scale":
      return applyScale(action, parameter, runtime, selection, errors);
    case "array":
      return applyArray(action, parameter, runtime, selection, errors);
    case "lookup":
      return applyLookup(action, parameter, ctx, selection, errors);
    case "polar-stretch":
      return applyPolarStretch(
        action,
        parameter,
        runtime,
        selection,
        errors,
        refusalSink(actionLabel(action), errors),
      );
  }
}

/* ── Tier 1 actions ─────────────────────────────────────────────── */

function applyMove(
  action: MoveAction,
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
  selection: readonly EntityLike[],
  errors: string[],
): ActionApplication {
  if (!isLinearLikeKind(parameter.kind)) {
    errors.push(
      `Action "${actionLabel(action)}" kind=move requires linear / polar / xy / constraint-horizontal / constraint-vertical parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  const delta = computeLinearLikeDelta(
    parameter,
    runtime,
    action.distanceMultiplier,
    action.angleOffset,
  );
  if (delta.x === 0 && delta.y === 0) {
    return { entities: selection, errors };
  }
  // No refusal sink: a pure TRANSLATION's linear part is the IDENTITY, so none
  // of the three heavy child kinds can decline it (an ACAD_TABLE keeps its size
  // and direction bit-exactly, an OLE2FRAME's map is diagonal, a heavy POLYLINE
  // moves whole). Same reason `expandArrayCopies` / `expandGridCopies` below
  // pass none — Array is built entirely out of translations.
  return {
    entities: selection.map((e) => translateEntity(e, delta)),
    errors,
  };
}

function applyStretch(
  action: StretchAction,
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
  selection: readonly EntityLike[],
  errors: string[],
): ActionApplication {
  if (!isLinearLikeKind(parameter.kind)) {
    errors.push(
      `Action "${actionLabel(action)}" kind=stretch requires linear / polar / xy / constraint-horizontal / constraint-vertical parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  const delta = computeLinearLikeDelta(
    parameter,
    runtime,
    action.distanceMultiplier,
    action.angleOffset,
    action.stretchDirection,
  );
  if (delta.x === 0 && delta.y === 0) {
    return { entities: selection, errors };
  }
  const refusals = refusalSink(actionLabel(action), errors);
  return {
    entities: selection.map((e) =>
      stretchEntity(e, action.crossingFrame, delta, refusals),
    ),
    errors,
  };
}

function applyFlip(
  action: FlipAction,
  parameter: DynamicParameter,
  selection: readonly EntityLike[],
  errors: string[],
): ActionApplication {
  if (parameter.kind !== "flip") {
    errors.push(
      `Action "${actionLabel(action)}" kind=flip requires flip parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  const flipParam = parameter as FlipParameter;
  if (!flipParam.currentValue) {
    return { entities: selection, errors };
  }
  const refusals = refusalSink(actionLabel(action), errors);
  return {
    entities: selection.map((e) =>
      mirrorEntity(e, flipParam.axisStart, flipParam.axisEnd, refusals),
    ),
    errors,
  };
}

/* ── Tier 2 actions (DYN-2) ─────────────────────────────────────── */

function applyRotate(
  action: RotateAction,
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
  selection: readonly EntityLike[],
  errors: string[],
): ActionApplication {
  if (!isAngularKind(parameter.kind)) {
    errors.push(
      `Action "${actionLabel(action)}" kind=rotate requires rotation / constraint-angular parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  // Both Rotation and AngularConstraint store currentAngle / defaultAngle
  // in degrees. The runtime resolver converts current → radians on
  // `rotationAngle`; we recompute defaultAngle in radians here.
  let center: Vec2;
  let runtimeAngleRad: number;
  let defaultAngleDeg: number;
  if (parameter.kind === "rotation") {
    const rot = parameter as RotationParameter;
    center = rot.basePoint;
    runtimeAngleRad = runtime.rotationAngle ?? rot.currentAngle * DEG_TO_RAD;
    defaultAngleDeg = rot.defaultAngle;
  } else {
    const ac = parameter as AngularConstraintParameter;
    center = ac.vertex;
    runtimeAngleRad = runtime.rotationAngle ?? ac.currentAngle * DEG_TO_RAD;
    defaultAngleDeg = ac.defaultAngle;
  }
  const defaultAngleRad = defaultAngleDeg * DEG_TO_RAD;
  const deltaRad = (runtimeAngleRad - defaultAngleRad) * action.angleMultiplier;
  if (deltaRad === 0) return { entities: selection, errors };
  const refusals = refusalSink(actionLabel(action), errors);
  return {
    entities: selection.map((e) => rotateEntity(e, center, deltaRad, refusals)),
    errors,
  };
}

function applyScale(
  action: ScaleAction,
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
  selection: readonly EntityLike[],
  errors: string[],
): ActionApplication {
  if (!isLinearLikeKind(parameter.kind)) {
    errors.push(
      `Action "${actionLabel(action)}" kind=scale requires linear / polar / xy / constraint-horizontal / constraint-vertical parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  const { sx: rawSx, sy: rawSy } = parameterScaleFactor(parameter, runtime);
  // Apply the action's scaleMultiplier to whichever component is active.
  // For Tier 2 constraint kinds the parameterScaleFactor already
  // pre-pinned the inactive axis to 1; pick the active axis as the
  // baseFactor. Tier 3 constraint kinds (aligned / radial / diameter)
  // share sx = sy, so baseFactor = rawSx is correct.
  let baseFactor: number;
  if (parameter.kind === "xy") {
    baseFactor =
      action.scaleType === "y-only"
        ? rawSy
        : action.scaleType === "x-only"
          ? rawSx
          : // uniform over xy — pick the larger deviation from 1 as the
            // dominant factor (AutoCAD "uniform scale" on xy uses avg).
            (rawSx + rawSy) / 2;
  } else if (parameter.kind === "constraint-horizontal") {
    baseFactor = rawSx;
  } else if (parameter.kind === "constraint-vertical") {
    baseFactor = rawSy;
  } else {
    baseFactor = rawSx;
  }
  const factor = baseFactor * action.scaleMultiplier;
  const sx = action.scaleType === "y-only" ? 1 : factor;
  const sy = action.scaleType === "x-only" ? 1 : factor;
  if (sx === 1 && sy === 1) return { entities: selection, errors };
  const center = parameterCenter(parameter);
  const refusals = refusalSink(actionLabel(action), errors);
  return {
    entities: selection.map((e) => scaleEntity(e, center, sx, sy, refusals)),
    errors,
  };
}

function applyArray(
  action: ArrayAction,
  parameter: DynamicParameter,
  runtime: ParameterRuntimeValue,
  selection: readonly EntityLike[],
  errors: string[],
): ActionApplication {
  if (!isLinearLikeKind(parameter.kind)) {
    errors.push(
      `Action "${actionLabel(action)}" kind=array requires linear / polar / xy / constraint-horizontal / constraint-vertical parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  const colSpacing = action.columnSpacing;
  const rowSpacing = action.rowSpacing;
  // AutoCAD computes instance count from the parameter distance / spacing.
  if (parameter.kind === "linear") {
    const lin = parameter as LinearParameter;
    if (colSpacing === 0) return { entities: selection, errors };
    const distance = runtime.distance ?? lin.currentDistance;
    const count = Math.max(
      1,
      Math.floor(Math.abs(distance) / Math.abs(colSpacing)) + 1,
    );
    const axisAngle = runtime.angle ?? 0;
    const step: Vec2 = {
      x: colSpacing * Math.cos(axisAngle),
      y: colSpacing * Math.sin(axisAngle),
    };
    return {
      entities: expandArrayCopies(selection, count, step),
      errors,
    };
  }
  if (parameter.kind === "polar") {
    const pol = parameter as PolarParameter;
    if (colSpacing === 0) return { entities: selection, errors };
    const distance = runtime.distance ?? pol.currentDistance;
    const count = Math.max(
      1,
      Math.floor(Math.abs(distance) / Math.abs(colSpacing)) + 1,
    );
    const angRad = runtime.angle ?? pol.currentAngle * DEG_TO_RAD;
    const step: Vec2 = {
      x: colSpacing * Math.cos(angRad),
      y: colSpacing * Math.sin(angRad),
    };
    return {
      entities: expandArrayCopies(selection, count, step),
      errors,
    };
  }
  if (
    parameter.kind === "constraint-horizontal" ||
    parameter.kind === "constraint-vertical"
  ) {
    // Constraint distance drives the array along its fixed axis.
    const c = parameter as
      | HorizontalConstraintParameter
      | VerticalConstraintParameter;
    if (colSpacing === 0) return { entities: selection, errors };
    const distance = runtime.distance ?? c.currentDistance;
    const count = Math.max(
      1,
      Math.floor(Math.abs(distance) / Math.abs(colSpacing)) + 1,
    );
    const step: Vec2 =
      parameter.kind === "constraint-horizontal"
        ? { x: colSpacing, y: 0 }
        : { x: 0, y: colSpacing };
    return {
      entities: expandArrayCopies(selection, count, step),
      errors,
    };
  }
  if (
    parameter.kind === "constraint-aligned" ||
    parameter.kind === "constraint-radial" ||
    parameter.kind === "constraint-diameter"
  ) {
    // DYN-3 Tier 3: 1D constraint drivers use their authored axis
    // (runtime.angle) as the array direction.
    if (colSpacing === 0) return { entities: selection, errors };
    const distance = runtime.distance ?? 0;
    const count = Math.max(
      1,
      Math.floor(Math.abs(distance) / Math.abs(colSpacing)) + 1,
    );
    const axisAngle = runtime.angle ?? 0;
    const step: Vec2 = {
      x: colSpacing * Math.cos(axisAngle),
      y: colSpacing * Math.sin(axisAngle),
    };
    return {
      entities: expandArrayCopies(selection, count, step),
      errors,
    };
  }
  // xy — 2D grid.
  const xy = parameter as XYParameter;
  const cx = runtime.xy?.x ?? xy.currentX;
  const cy = runtime.xy?.y ?? xy.currentY;
  const cols =
    colSpacing === 0
      ? 1
      : Math.max(1, Math.floor(Math.abs(cx) / Math.abs(colSpacing)) + 1);
  const rows =
    rowSpacing === 0
      ? 1
      : Math.max(1, Math.floor(Math.abs(cy) / Math.abs(rowSpacing)) + 1);
  if (cols === 1 && rows === 1) return { entities: selection, errors };
  return {
    entities: expandGridCopies(selection, cols, rows, colSpacing, rowSpacing),
    errors,
  };
}

function expandArrayCopies(
  selection: readonly EntityLike[],
  count: number,
  step: Vec2,
): EntityLike[] {
  const out: EntityLike[] = [];
  for (const e of selection) {
    for (let i = 0; i < count; i++) {
      if (i === 0) {
        out.push(e);
      } else {
        out.push(translateEntity(e, vecScale(step, i)));
      }
    }
  }
  return out;
}

function expandGridCopies(
  selection: readonly EntityLike[],
  cols: number,
  rows: number,
  colSpacing: number,
  rowSpacing: number,
): EntityLike[] {
  const out: EntityLike[] = [];
  for (const e of selection) {
    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        if (r === 0 && c === 0) {
          out.push(e);
        } else {
          out.push(
            translateEntity(e, {
              x: c * colSpacing,
              y: r * rowSpacing,
            }),
          );
        }
      }
    }
  }
  return out;
}

/* ── Tier 3 actions (DYN-3) ─────────────────────────────────────── */

// Polar Stretch implementation lives in actions.polarStretch.ts to keep
// this file under the 1000-LOC guideline (CLAUDE.md). See there for
// docstring / degenerate-case notes.

/**
 * Lookup action: finds the lookup table and the matching row based on the
 * Lookup parameter's currentLookupRowId, then emits runtimeValueUpdates for
 * every parameter listed in the table's input columns, each set to the
 * row's inputValues[columnId]. The evaluator re-runs the pipeline after
 * applying these updates.
 *
 * Does NOT transform entities — returns the selection unchanged.
 *
 * Edge cases (all handled defensively — return selection + error, never
 * crash):
 *   - parameter kind ≠ lookup: error
 *   - ctx.lookupTables missing: error
 *   - table id unknown: error
 *   - current row id undefined or unknown: silent no-op (user just hasn't
 *     selected a row yet)
 */
function applyLookup(
  action: LookupAction,
  parameter: DynamicParameter,
  ctx: ActionContext,
  selection: readonly EntityLike[],
  errors: string[],
): ActionApplication {
  if (parameter.kind !== "lookup") {
    errors.push(
      `Action "${actionLabel(action)}" kind=lookup requires lookup parameter, got ${parameter.kind}`,
    );
    return { entities: selection, errors };
  }
  const tables = ctx.lookupTables ?? [];
  const lookupParam = parameter as LookupParameter;
  const table = findLookupTableById(tables, lookupParam.lookupTableId);
  if (!table) {
    errors.push(
      `Action "${actionLabel(action)}" references unknown lookup table ${lookupParam.lookupTableId}`,
    );
    return { entities: selection, errors };
  }
  const rowId = lookupParam.currentLookupRowId;
  if (!rowId) {
    return { entities: selection, errors };
  }
  const row = findLookupRowById(table, rowId);
  if (!row) {
    return { entities: selection, errors };
  }
  // Build runtimeValueUpdates from the row's inputValues.
  const updates: RuntimeValueUpdate[] = [];
  for (const col of table.inputColumns) {
    const v = row.inputValues[col.id];
    if (v === undefined) continue;
    updates.push({
      parameterId: col.parameterId,
      property: col.propertyKey,
      value: v,
    });
  }
  return {
    entities: selection,
    errors,
    runtimeValueUpdates: updates,
  };
}

/* ── XY parameter translate helper (direct delta application) ───────── */
//
// AutoCAD exposes the two-axis XY Parameter as a single grip that the user
// drags in 2D; both X and Y components flow to the driven entities
// independently. The canonical way to wire this in a dynamic block is a
// Move action attached to the XY parameter — see `applyMove` above, which
// computes `delta = (currentX - defaultX, currentY - defaultY)` via
// `computeLinearLikeDelta` and translates the selection.
//
// `translateXyParameterSelection` is a thin, parameter-driven convenience
// wrapper that exposes exactly that translate-xy behavior without an
// action-dispatch round-trip: given an `XYParameter` and a selection, it
// returns the same-length array of translated entities. No action record,
// no evaluator graph — useful for:
//
//   * unit tests validating the XY→translate contract in isolation
//   * authoring-time previews while the user is dragging a grip (shows
//     the displacement before the user commits to a Move action binding)
//   * programmatic DRY callers that don't own an ActionContext
//
// Semantics (100% AutoCAD-parity, matches `applyMove` on an XY parameter
// with `distanceMultiplier = 1`, `angleOffset = 0`):
//
//   deltaX = (runtime.xy?.x ?? parameter.currentX) - parameter.defaultX
//   deltaY = (runtime.xy?.y ?? parameter.currentY) - parameter.defaultY
//
// ValueSet snapping is applied by the caller via
// `resolveParameterRuntimeValue` (same pipeline the Move action uses) —
// pass the resolved runtime to honor increment / list snap rules.
// When `runtime` is omitted we fall back to the parameter's raw currents
// (no snapping), matching the delta you'd see immediately after a grip
// edit before the next evaluator pass.
export function translateXyParameterSelection<T extends EntityLike>(
  parameter: XYParameter,
  selection: readonly T[],
  runtime?: ParameterRuntimeValue,
): readonly T[] {
  const curX = runtime?.xy?.x ?? parameter.currentX;
  const curY = runtime?.xy?.y ?? parameter.currentY;
  const delta: Vec2 = {
    x: curX - parameter.defaultX,
    y: curY - parameter.defaultY,
  };
  if (delta.x === 0 && delta.y === 0) return selection;
  return selection.map((e) => translateEntity(e, delta));
}
